// =====================================================================
// Demandes de lien
// =====================================================================
// « X t'a ajouté comme son frère. Le connaissez-vous ? »
//
// Celui qui ajoute connaît déjà la personne : il a saisi son numéro. Il
// n'y a donc rien à deviner, seulement à confirmer ou refuser.
//
// Un refus ne crée rien : le nœud reste dans l'arbre privé du déclarant,
// non rattaché, et la demande ne peut pas être renvoyée. Le silence ne
// vaut jamais accord : la demande expire.
// =====================================================================

import { useEffect, useState } from 'react';
import { api, ErreurApi } from '../api';
import {
  LIBELLE_RELATION_DECLAREE,
  type DemandeLien,
} from '../../partage/types';
import {
  Alerte,
  Chargement,
  IconeEnveloppe,
  Vide,
  initiales,
  nomComplet,
} from '../composants/base';

type Envoyee = DemandeLien & {
  cible_nom: string | null;
  cible_prenoms: string | null;
};

export function Demandes({ onChangement }: { onChangement?: () => void }) {
  const [liste, setListe] = useState<DemandeLien[] | null>(null);
  const [envoyees, setEnvoyees] = useState<Envoyee[]>([]);
  const [vue, setVue] = useState<'recues' | 'envoyees'>('recues');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);

  async function charger() {
    try {
      const [recues, parties] = await Promise.all([
        api.demandes(),
        api.demandesEnvoyees(),
      ]);
      setListe(recues);
      setEnvoyees(parties);
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Chargement impossible.');
    }
  }

  async function annuler(id: string) {
    setErreur(null);
    setEnCours(id);
    try {
      await api.annulerDemande(id);
      await charger();
      onChangement?.();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Annulation impossible.');
    } finally {
      setEnCours(null);
    }
  }

  useEffect(() => {
    void charger();
  }, []);

  async function repondre(id: string, accepte: boolean) {
    setErreur(null);
    setEnCours(id);
    try {
      await api.repondreDemande(id, accepte);
      await charger();
      onChangement?.();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Action impossible.');
    } finally {
      setEnCours(null);
    }
  }

  if (erreur && liste === null) return <Alerte>{erreur}</Alerte>;
  if (liste === null) return <Chargement />;

  const enAttente = envoyees.filter((d) => d.statut === 'en_attente');

  return (
    <>
      <div className="bascule" role="tablist" aria-label="Affichage">
        <button
          role="tab"
          aria-selected={vue === 'recues'}
          onClick={() => setVue('recues')}
        >
          Reçues{liste.length > 0 ? ` · ${liste.length}` : ''}
        </button>
        <button
          role="tab"
          aria-selected={vue === 'envoyees'}
          onClick={() => setVue('envoyees')}
        >
          Envoyées{enAttente.length > 0 ? ` · ${enAttente.length}` : ''}
        </button>
      </div>

      {erreur && <Alerte>{erreur}</Alerte>}

      {vue === 'envoyees' ? (
        envoyees.length === 0 ? (
          <Vide icone={<IconeEnveloppe />}>
            Vous n'avez envoyé aucune demande. Quand vous ajoutez un proche
            avec son adresse ou son numéro, elle apparaît ici.
          </Vide>
        ) : (
          envoyees.map((d) => (
            <div className="carte" key={d.id}>
              <div style={{ fontWeight: 580 }}>
                {nomComplet(d.cible_nom, d.cible_prenoms)}
              </div>
              <p className="doux" style={{ marginBottom: 12 }}>
                Déclaré comme {LIBELLE_RELATION_DECLAREE[d.relation]} ·{' '}
                {d.statut === 'en_attente'
                  ? 'en attente de sa réponse'
                  : d.statut === 'confirmee'
                    ? 'confirmé'
                    : d.statut === 'refusee'
                      ? 'refusé'
                      : 'expiré'}
              </p>
              {d.statut === 'en_attente' && (
                <button
                  className="bouton danger mince"
                  onClick={() => annuler(d.id)}
                  disabled={enCours === d.id}
                >
                  {enCours === d.id ? 'Annulation…' : 'Annuler cette demande'}
                </button>
              )}
            </div>
          ))
        )
      ) : liste.length === 0 ? (
        <Vide icone={<IconeEnveloppe />}>
          Aucune demande en attente. Quand un proche vous ajoutera à son arbre,
          vous le verrez ici.
        </Vide>
      ) : (
        liste.map((d) => {
          const nom = nomComplet(d.declarant_nom, d.declarant_prenoms);
          const precision =
            d.relation === 'frere_soeur_un_parent' && d.parent_partage
              ? d.parent_partage === 'pere'
                ? ' (même père)'
                : ' (même mère)'
              : '';

          return (
            <div className="carte" key={d.id}>
              <div className="masque-entete">
                <div className="jeton" aria-hidden="true">
                  {initiales(d.declarant_nom, d.declarant_prenoms)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 580 }}>{nom}</div>
                  <div className="doux">vous a ajouté à son arbre</div>
                </div>
              </div>

              <p>
                <strong>{nom}</strong> vous déclare comme{' '}
                <strong>{LIBELLE_RELATION_DECLAREE[d.relation]}</strong>
                {precision}.
              </p>
              <p className="doux" style={{ marginBottom: 14 }}>
                Le connaissez-vous ? Si vous confirmez, vos deux arbres se
                relient. Si vous refusez, rien n'est créé et la demande ne
                pourra pas être renvoyée.
              </p>

              <div className="rangee-boutons">
                <button
                  className="bouton danger"
                  onClick={() => repondre(d.id, false)}
                  disabled={enCours === d.id}
                >
                  Je ne le connais pas
                </button>
                <button
                  className="bouton"
                  onClick={() => repondre(d.id, true)}
                  disabled={enCours === d.id}
                >
                  {enCours === d.id ? 'Un instant…' : 'Oui, confirmer'}
                </button>
              </div>
            </div>
          );
        })
      )}
    </>
  );
}
